"use client"

import { useRef, useState, type MouseEvent, type ReactNode } from "react"
import { useRouter } from "next/navigation"
import {
  Meeting,
  Notification,
  NotificationNotifiableComment,
  NotificationNotifiableNote,
  NotificationNotifiableTask,
} from "@/schema"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"
import { formatElapsedCompact } from "@/utils/format-date"
import InfiniteScroll from "@ui/infinite-scroll"
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/shad"
import { Bell, Eye, EyeOff } from "lucide-react"

import { isAllBooksId } from "@/lib/all-books"
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useMarkNotificationAsUnread,
} from "@/hooks/api/notifications/mutations"
import { useInfiniteNotifications, useUnreadNotificationsCount } from "@/hooks/api/notifications/queries"
import { useBookId } from "@/hooks/use-book-id"
import { useNotificationsCable } from "@/hooks/useNotificationsCable"
import { BookIcon } from "@/components/books/book-icon"
import MeetingDetailsPanel from "@/components/calendar/meeting-details-panel"
import { useNoteDialog } from "@/components/notes/use-note-dialog"
import { SignedDocumentDialog } from "@/components/notifications/signed-document-dialog"
import TaskDetailSlideover from "@/components/tasks/detail-slideover/task-detail-slideover"

function NotificationItem({
  n,
  onOpen,
  onMarkRead,
  onMarkUnread,
  isMutating,
  showBookName,
}: {
  n: Notification
  onOpen: () => void
  onMarkRead: () => void
  onMarkUnread: () => void
  isMutating: boolean
  showBookName: boolean
}) {
  const handleToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (n.read) {
      onMarkUnread()
    } else {
      onMarkRead()
    }
  }

  const tooltip = n.read ? "Mark as unread" : "Mark as read"
  const ariaLabel = n.read ? "Mark notification as unread" : "Mark notification as read"
  const buttonVisibilityClasses = isMutating
    ? "opacity-100 pointer-events-auto"
    : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
  const iconClassName = n.read ? "text-muted-foreground" : "text-primary"

  return (
    <div
      className="group hover:bg-muted/50 relative flex w-full cursor-pointer items-start gap-3 px-4 py-3 pr-6"
      onClick={onOpen}
    >
      <div className="self-center">
        <div className={`h-2.5 w-2.5 rounded-full ${n.read ? "bg-muted" : "bg-primary"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm break-words">{n.message}</div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {showBookName && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <BookIcon
                    bookId={n.book_id}
                    initials={n.book_initials}
                    name={n.book_name}
                    className="bg-muted size-4 cursor-default rounded"
                    textClassName="text-[9px] font-medium"
                  />
                </TooltipTrigger>
                <TooltipContent side="left">{n.book_name}</TooltipContent>
              </Tooltip>
            )}
            <div className="text-muted-foreground text-xs">{formatElapsedCompact(n.created_at)}</div>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconXs"
          disableInteraction
          className={`bg-background absolute top-1/2 left-2 -translate-y-1/2 transition-opacity ${buttonVisibilityClasses}`}
          tooltip={tooltip}
          aria-label={ariaLabel}
          aria-pressed={n.read}
          onClick={handleToggleClick}
          disabled={isMutating}
        >
          {n.read ? <EyeOff className={iconClassName} /> : <Eye className={iconClassName} />}
        </Button>
        {n.notifiable?.type === "Comment" && (n.notifiable as NotificationNotifiableComment)?.content && (
          <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
            {(n.notifiable as NotificationNotifiableComment).content}
          </div>
        )}
        {n.notifiable?.type === "Task" && (n.notifiable as NotificationNotifiableTask)?.title && (
          <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
            {(n.notifiable as NotificationNotifiableTask).title}
          </div>
        )}
        {n.notifiable?.type === "Note" && (n.notifiable as NotificationNotifiableNote)?.content && (
          <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
            {(n.notifiable as NotificationNotifiableNote).content}
          </div>
        )}
      </div>
    </div>
  )
}

type NotificationMenuProps = {
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode | ((unreadCount: number) => ReactNode)
}

export default function NotificationMenu({ onOpenChange, trigger }: NotificationMenuProps) {
  const [open, setOpen] = useState(false)
  const [signedDocDialog, setSignedDocDialog] = useState<{
    open: boolean
    notification: Notification | null
  }>({ open: false, notification: null })
  useNotificationsCable(true)

  const bookId = useBookId()
  const showBookName = isAllBooksId(bookId)

  const { data: unreadCount = 0 } = useUnreadNotificationsCount()
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteNotifications()
  const markRead = useMarkNotificationAsRead()
  const markUnread = useMarkNotificationAsUnread()
  const markAll = useMarkAllNotificationsAsRead()
  const { openSlideover } = useGlobalSlideoverStore()
  const { open: openNoteDialog } = useNoteDialog()
  const router = useRouter()

  const notifications = data?.notifications || []

  const updateOpen = (nextOpen: boolean) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const openFromNotification = (n: Notification) => {
    if (!n.read) {
      markRead.mutate(n.id)
    }

    if (n.notifiable?.type === "DocusignEnvelope") {
      setSignedDocDialog({ open: true, notification: n })
      updateOpen(false)
      return
    }

    if (n.notifiable?.type === "Note") {
      const noteId = String(n.notifiable.id)
      if (bookId === n.book_id || isAllBooksId(bookId)) {
        openNoteDialog({ noteId })
        updateOpen(false)
        return
      }

      if (n.frontend_path) {
        router.push(n.frontend_path)
        updateOpen(false)
        return
      }

      openNoteDialog({ noteId })
      updateOpen(false)
      return
    }

    if (n.notifiable?.type === "Task") {
      const taskId = String(n.notifiable.id)
      openSlideover({ content: <TaskDetailSlideover taskId={taskId} /> })
      updateOpen(false)
      return
    }

    if (n.notifiable?.type === "Comment") {
      const comment = n.notifiable as NotificationNotifiableComment
      if (comment.commentable_type === "Task" && comment.commentable_id) {
        openSlideover({
          content: <TaskDetailSlideover taskId={String(comment.commentable_id)} initialTab="comments" />,
        })
        updateOpen(false)
        return
      }
      if (comment.commentable_type === "Meeting" && comment.commentable_id) {
        if (bookId !== n.book_id && !isAllBooksId(bookId) && n.frontend_path) {
          router.push(n.frontend_path)
          updateOpen(false)
          return
        }

        openSlideover({
          content: (
            <MeetingDetailsPanel
              meeting={{ id: String(comment.commentable_id) } as Meeting}
              onDelete={() => useGlobalSlideoverStore.getState().closeSlideover()}
              initialTab="comments"
            />
          ),
        })
        updateOpen(false)
        return
      }
    }

    if (n.frontend_path) {
      router.push(n.frontend_path)
      updateOpen(false)
    }
  }

  const viewportRef = useRef<HTMLDivElement | null>(null)

  const triggerNode = typeof trigger === "function" ? (trigger as (c: number) => ReactNode)(unreadCount) : trigger

  return (
    <>
      <Popover open={open} onOpenChange={updateOpen}>
        <PopoverTrigger asChild>
          {triggerNode ?? (
            <Button variant="ghost" size="iconSm" className="relative">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="bg-destructive text-destructive-foreground absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          side="right"
          alignOffset={-40}
          sideOffset={10}
          align="start"
          className="w-[420px] max-w-[95vw] p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <TooltipProvider delayDuration={0}>
            <div className="flex flex-col">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="text-sm font-semibold">Notifications</div>
                <Button variant="ghost" size="sm" onClick={() => markAll.mutate()} disabled={unreadCount === 0}>
                  Mark all as read
                </Button>
              </div>

              <ScrollArea className="h-[480px]" viewportRef={viewportRef}>
                {isLoading && (
                  <div className="space-y-2 p-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                )}
                {!isLoading && notifications.length === 0 && (
                  <div className="text-muted-foreground py-6 text-center text-sm">You&apos;re all caught up</div>
                )}
                <div className="flex flex-col">
                  {notifications.map((n) => (
                    <NotificationItem
                      key={n.id}
                      n={n}
                      onOpen={() => openFromNotification(n)}
                      onMarkRead={() => markRead.mutate(n.id)}
                      onMarkUnread={() => markUnread.mutate(n.id)}
                      isMutating={
                        (markRead.isPending && markRead.variables === n.id) ||
                        (markUnread.isPending && markUnread.variables === n.id)
                      }
                      showBookName={showBookName}
                    />
                  ))}
                </div>
                <InfiniteScroll
                  isFetchingNextPage={!!isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={!!hasNextPage}
                  root={viewportRef.current}
                />
              </ScrollArea>
            </div>
          </TooltipProvider>
        </PopoverContent>
      </Popover>
      <SignedDocumentDialog
        open={signedDocDialog.open}
        setOpen={(open) => setSignedDocDialog((prev) => ({ ...prev, open }))}
        notification={signedDocDialog.notification}
      />
    </>
  )
}
